import React, { useState } from 'react'
import { IconButton, Menu, MenuItem } from '@mui/material'
import BusinessIcon from '@mui/icons-material/Business'
import { type Building } from 'services'

export interface BuildingIconButtonProps {
  buildings: Building[]
  disabled?: boolean
  loading?: boolean
  onBuildingSelect: (building: Building) => void
}

const buildingLabel = (building: Building): string =>
  building.displayName ?? building.buildingName ?? `Building ${building.id}`

export const BuildingIconButton: React.FC<BuildingIconButtonProps> = ({
  buildings,
  disabled = false,
  loading = false,
  onBuildingSelect
}) => {
  const [buildingMenuAnchor, setBuildingMenuAnchor] = useState<null | HTMLElement>(null)

  const handleBuildingSelect = (building: Building): void => {
    onBuildingSelect(building)
    setBuildingMenuAnchor(null)
  }

  const sortedBuildings = [...buildings].sort((a, b) =>
    buildingLabel(a).localeCompare(buildingLabel(b), 'en', { sensitivity: 'base' })
  )

  return (
    <>
      <IconButton
        disabled={disabled || loading}
        onClick={(e) => { setBuildingMenuAnchor(e.currentTarget) }}
      >
        <BusinessIcon />
      </IconButton>
      <Menu
        anchorEl={buildingMenuAnchor}
        open={Boolean(buildingMenuAnchor)}
        onClose={() => { setBuildingMenuAnchor(null) }}
        PaperProps={{
          style: {
            maxHeight: 300,
            minWidth: 250
          }
        }}
      >
        {buildings.length === 0
          ? (
              <MenuItem disabled>
                {loading ? 'Loading buildings...' : 'No buildings available'}
              </MenuItem>
            )
          : (
              sortedBuildings.map((building) => (
                <MenuItem
                  key={building.id}
                  onClick={() => { handleBuildingSelect(building) }}
                >
                  {buildingLabel(building)}
                </MenuItem>
              ))
            )}
      </Menu>
    </>
  )
}
